import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { sleepPlanService } from '../../services/sleepPlanService';
import { babyProfileService } from '../../services/babyProfileService';
import Button from '../../components/common/Button';
import { useLogger } from '../../utils/logger';
import CollapsibleSafetyGuidelines from '../../components/disclaimers/CollapsibleSafetyGuidelines';
import ParentSupportBox from '../../components/mental-health/ParentSupportBox';
import ReactMarkdown from 'react-markdown';

// Add print-specific styles
const PrintStyles = styled.style`
  @media print {
    body * {
      visibility: hidden;
    }
    .container, .container * {
      visibility: visible;
    }
    .container {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
    }
    .back-link, .ButtonGroup, nav, footer {
      display: none !important;
    }
    .PlanCard {
      box-shadow: none !important;
      margin: 0 !important;
      padding: 0 !important;
    }
  }
`;

// Styled Components
const PageContainer = styled.div`
  padding: 2rem 0;
  max-width: 1000px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  .back-link {
    color: var(--primary);
    text-decoration: none;
    display: flex;
    align-items: center;
    font-weight: 500;
    transition: all 0.2s ease;
    
    &:hover {
      color: var(--primary-dark);
      transform: translateX(-3px);
    }
    
    i {
      margin-right: 8px;
      font-size: 1.1rem;
    }
  }
`;

const PlanCard = styled.div`
  background-color: white;
  border-radius: 20px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  padding: 3rem;
  margin-bottom: 2rem;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 8px;
    background: linear-gradient(to right, var(--primary), var(--secondary));
  }
`;

const WelcomeHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -2rem;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 4px;
    background: linear-gradient(to right, var(--secondary-light), var(--secondary));
    border-radius: 2px;
  }
  
  h1 {
    color: var(--primary);
    margin-bottom: 1.5rem;
    font-size: 2.8rem;
    font-weight: 700;
    
    @media (max-width: 768px) {
      font-size: 2.2rem;
    }
  }
  
  .baby-details {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 1.5rem;
    margin: 2rem 0;
    
    @media (max-width: 576px) {
      flex-direction: column;
      align-items: center;
      gap: 1rem;
    }
    
    .detail-item {
      background-color: #f8f9fa;
      padding: 0.75rem 1.75rem;
      border-radius: 50px;
      display: flex;
      align-items: center;
      box-shadow: 0 3px 10px rgba(0, 0, 0, 0.04);
      transition: transform 0.2s ease, box-shadow 0.2s ease;
      
      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
      }
      
      .icon {
        color: var(--primary);
        margin-right: 0.75rem;
        font-size: 1.1rem;
      }
      
      .label {
        font-weight: 600;
        margin-right: 0.5rem;
        color: var(--gray);
      }
      
      .value {
        color: var(--dark);
        font-weight: 500;
      }
    }
  }
  
  .plan-date {
    color: var(--gray);
    font-size: 0.9rem;
    margin-top: 1rem;
  }
  
  .trusted-sources {
    max-width: 800px;
    margin: 1.5rem auto 0;
    color: var(--gray);
    font-size: 1rem;
    line-height: 1.7;
    background-color: rgba(126, 203, 185, 0.08);
    padding: 1.5rem;
    border-radius: 12px;
    border-left: 4px solid var(--secondary);
  }
`;

// eslint-disable-next-line no-unused-vars
const SleepOverview = styled.div`
  background-color: #f8f9fa;
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 3rem;
  border-left: 5px solid var(--secondary);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  
  p {
    margin: 0;
    color: var(--dark);
    line-height: 1.7;
    font-size: 1.15rem;
  }
`;

// eslint-disable-next-line no-unused-vars
const SectionTitle = styled.h2`
  color: ${props => props.color || 'var(--primary)'};
  margin-bottom: 1.75rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid rgba(233, 64, 87, 0.2);
  font-size: 1.9rem;
  font-weight: 700;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 80px;
    height: 2px;
    background-color: var(--primary);
  }
`;

// eslint-disable-next-line no-unused-vars
const ScheduleTable = styled.div`
  border: none;
  border-radius: 16px;
  overflow: hidden;
  margin-bottom: 3rem;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.06);
  
  .schedule-header {
    background: linear-gradient(to right, var(--primary-light), var(--primary));
    padding: 1.25rem 1.75rem;
    font-weight: 600;
    display: grid;
    grid-template-columns: 1fr 2fr 2fr;
    color: white;
    
    @media (max-width: 576px) {
      padding: 1rem;
    }
  }
  
  .schedule-row {
    display: grid;
    grid-template-columns: 1fr 2fr 2fr;
    border-top: 1px solid #f0f0f0;
    transition: background-color 0.2s ease;
    
    &:hover {
      background-color: rgba(126, 203, 185, 0.08);
    }
    
    &:nth-child(even) {
      background-color: rgba(248, 249, 250, 0.7);
      
      &:hover {
        background-color: rgba(126, 203, 185, 0.08);
      }
    }
    
    .time {
      padding: 1.25rem 1.75rem;
      font-weight: 600;
      color: var(--primary);
      
      @media (max-width: 576px) {
        padding: 1rem;
      }
    }
    
    .activity {
      padding: 1.25rem 1.75rem;
      
      @media (max-width: 576px) {
        padding: 1rem;
      }
    }
    
    .notes {
      padding: 1.25rem 1.75rem;
      color: var(--gray);
      font-style: italic;
      font-size: 0.95rem;
      
      @media (max-width: 576px) {
        padding: 1rem;
      }
    }
  }
`;

// eslint-disable-next-line no-unused-vars
const BedtimeRoutine = styled.div`
  margin-bottom: 3rem;
  
  .routine-list {
    background-color: #fff;
    border-radius: 16px;
    padding: 0.75rem;
    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.06);
  }
  
  .routine-item {
    display: flex;
    align-items: center;
    padding: 1.25rem;
    border-bottom: 1px solid #f0f0f0;
    transition: transform 0.2s ease, background-color 0.2s ease;
    
    &:hover {
      background-color: rgba(126, 203, 185, 0.05);
      transform: translateX(5px);
    }
    
    &:last-child {
      border-bottom: none;
    }
    
    .check-icon {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: linear-gradient(to bottom right, var(--purple), var(--purple-dark));
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 1.25rem;
      flex-shrink: 0;
      box-shadow: 0 3px 8px rgba(139, 110, 180, 0.3);
    }
    
    .content {
      flex: 1;
      
      .title {
        font-weight: 600;
        color: var(--dark);
        margin-bottom: 0.5rem;
        font-size: 1.1rem;
      }
      
      .description {
        color: var(--gray);
        font-size: 1rem;
        line-height: 1.6;
      }
    }
  }
`;

// eslint-disable-next-line no-unused-vars
const TipsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  margin-bottom: 3rem;
  
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
  
  @media (min-width: 992px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
`;

// eslint-disable-next-line no-unused-vars
const TipCard = styled.div`
  background-color: #fff;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.07);
  border-top: 5px solid ${props => props.color || 'var(--primary)'};
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.1);
  }
  
  .tip-icon {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background-color: ${props => props.iconBg || 'rgba(233, 64, 87, 0.1)'};
    color: ${props => props.iconColor || 'var(--primary)'};
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1.5rem;
    font-size: 1.5rem;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  }
  
  h4 {
    color: ${props => props.titleColor || 'var(--dark)'};
    margin-bottom: 1rem;
    font-size: 1.3rem;
    font-weight: 600;
  }
  
  p {
    margin-bottom: 0;
    color: var(--gray);
    font-size: 1rem;
    line-height: 1.7;
    flex-grow: 1;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  margin-top: 3rem;
  
  @media (max-width: 576px) {
    flex-direction: column;
    
    button {
      width: 100%;
      padding: 0.75rem 1.5rem;
      font-size: 1rem;
    }
  }
`;

// eslint-disable-next-line no-unused-vars
const DisclaimerBox = styled.div`
  background-color: #f8f9fa;
  border-left: 5px solid var(--secondary);
  padding: 1.5rem;
  margin-top: 3rem;
  border-radius: 8px;
  font-size: 0.95rem;
  color: var(--gray);
  line-height: 1.6;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.04);
  
  strong {
    color: var(--dark);
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 5rem 2rem;
  background-color: white;
  border-radius: 20px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  
  h3 {
    color: var(--primary);
    margin-bottom: 1.5rem;
    font-size: 1.8rem;
    font-weight: 600;
  }
  
  p {
    color: var(--gray);
    margin-bottom: 2.5rem;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
    font-size: 1.1rem;
    line-height: 1.7;
  }
  
  .spinner {
    width: 60px;
    height: 60px;
    border: 6px solid rgba(233, 64, 87, 0.2);
    border-radius: 50%;
    border-top-color: var(--primary);
    animation: spin 1s ease-in-out infinite;
    margin: 0 auto 2.5rem;
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

// eslint-disable-next-line no-unused-vars
const GeneratingPlanState = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  background-color: white;
  border-radius: 16px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  margin: 2rem 0;
  
  h3 {
    color: var(--primary);
    margin-bottom: 1rem;
    font-size: 1.5rem;
    font-weight: 600;
  }
  
  p {
    color: var(--gray);
    margin-bottom: 2rem;
    max-width: 500px;
    margin-left: auto;
    margin-right: auto;
    line-height: 1.6;
  }
  
  .spinner {
    width: 60px;
    height: 60px;
    border: 5px solid rgba(233, 64, 87, 0.2);
    border-radius: 50%;
    border-top-color: var(--primary);
    animation: spin 1s ease-in-out infinite;
    margin: 0 auto 2rem;
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 5rem 2rem;
  background-color: white;
  border-radius: 20px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  
  h3 {
    color: var(--primary);
    margin-bottom: 1.5rem;
    font-size: 1.8rem;
    font-weight: 600;
  }
  
  p {
    color: var(--gray);
    margin-bottom: 2.5rem;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
    font-size: 1.1rem;
    line-height: 1.7;
  }
  
  .error-icon {
    font-size: 4rem;
    color: var(--primary);
    margin-bottom: 1.5rem;
    opacity: 0.8;
  }
  
  button {
    padding: 0.75rem 2rem;
    font-size: 1rem;
  }
`;

const ContentSection = styled.div`
  margin-bottom: 3.5rem;
  
  h3 {
    color: var(--primary);
    margin-bottom: 1.5rem;
    font-size: 1.6rem;
    font-weight: 600;
    position: relative;
    padding-bottom: 0.75rem;
    
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 60px;
      height: 3px;
      background-color: var(--secondary);
      border-radius: 2px;
    }
  }
  
  p {
    line-height: 1.8;
    margin-bottom: 1.5rem;
    font-size: 1.05rem;
    color: #4a4a4a;
  }
  
  ul {
    margin-bottom: 1.5rem;
    padding-left: 1.75rem;
    
    li {
      margin-bottom: 0.75rem;
      line-height: 1.7;
      position: relative;
      
      &::marker {
        color: var(--secondary);
      }
    }
  }
  
  a {
    color: var(--primary);
    text-decoration: none;
    font-weight: 500;
    transition: color 0.2s ease;
    
    &:hover {
      color: var(--primary-dark);
      text-decoration: underline;
    }
  }
  
  blockquote {
    border-left: 4px solid var(--secondary-light);
    padding: 1rem 1.5rem;
    margin: 1.5rem 0;
    background-color: rgba(126, 203, 185, 0.08);
    font-style: italic;
    color: #555;
  }
  
  strong {
    color: var(--dark);
    font-weight: 600;
  }
`;

const SleepPlanViewer = () => {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sleepPlan, setSleepPlan] = useState(null);
  const [profile, setProfile] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [currentDate] = useState(new Date().toLocaleDateString('en-AU', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }));

  useEffect(() => {
    const fetchSleepPlan = async () => {
      try {
        setError(null);
        
        // Get the user's ID token from sessionStorage instead of calling getIdToken
        const token = sessionStorage.getItem('idToken');
        
        if (!token) {
          throw new Error('Authentication token not found. Please sign in again.');
        }
        
        // Fetch the sleep plan
        const sleepPlanData = await sleepPlanService.getSleepPlan(token, profileId);
        console.log('Sleep plan data:', sleepPlanData);
        
        // Set the sleep plan and profile data
        setSleepPlan(sleepPlanData.sleepPlan);
        setProfile(sleepPlanData.profile);
        
        // If we have content, clear the polling interval
        if (sleepPlanData.sleepPlan && sleepPlanData.sleepPlan.content) {
          if (pollingInterval) {
            clearInterval(pollingInterval);
            setPollingInterval(null);
          }
        }
        
      } catch (error) {
        console.error('Error fetching sleep plan:', error);
        setError(error.message || 'Failed to load sleep plan');
      } finally {
        setLoading(false);
      }
    };

    if (currentUser && profileId) {
      // Initial fetch
      fetchSleepPlan();
      
      // Set up polling if not already polling and we don't have content yet
      if (!pollingInterval && (!sleepPlan || !sleepPlan.content)) {
        const interval = setInterval(() => {
          // Only poll if we don't have content yet
          if (!sleepPlan?.content) {
            console.log('Polling for sleep plan updates...');
            fetchSleepPlan();
          } else {
            // Clear interval if we have content
            clearInterval(interval);
            setPollingInterval(null);
          }
        }, 10000); // Poll every 10 seconds
        
        setPollingInterval(interval);
      }
    }
    
    // Clean up interval on unmount
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [currentUser, profileId]);  // Remove sleepPlan?.content and pollingInterval from dependencies

  // Calculate age from birthdate
  const calculateAge = (birthDate) => {
    if (!birthDate) return '';
    
    return babyProfileService.calculateAge(birthDate);
  };

  // Handle update profile button click
  // eslint-disable-next-line no-unused-vars
  const handleUpdateProfile = () => {
    navigate(`/edit-child/${profileId}`);
  };

  // Handle download plan button click
  // eslint-disable-next-line no-unused-vars
  const handleDownloadPlan = () => {
    // In a real app, this would generate a PDF or printable version
    alert('This feature will be implemented in a future update.');
  };

  // Handle print plan button click
  const handlePrintPlan = () => {
    window.print();
  };

  // Render loading state
  if (loading) {
    return (
      <div className="container">
        <PageContainer>
          <PageHeader>
            <Link to="/dashboard" className="back-link">
              <i className="fas fa-arrow-left me-2"></i> Back to Dashboard
            </Link>
          </PageHeader>
          
          <LoadingState>
            <div className="spinner"></div>
            <h3>Loading Your Sleep Plan</h3>
            <p>
              We're retrieving your personalized sleep plan.
              This process typically takes about 60 seconds to complete. Thank you for your patience!
            </p>
          </LoadingState>
        </PageContainer>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="container">
        <PageContainer>
          <PageHeader>
            <Link to="/dashboard" className="back-link">
              <i className="fas fa-arrow-left me-2"></i> Back to Dashboard
            </Link>
          </PageHeader>
          
          <ErrorState>
            <div className="error-icon">
              <i className="fas fa-exclamation-circle"></i>
            </div>
            <h3>Oops! Something went wrong</h3>
            <p>{error}</p>
            <Button variant="primary" onClick={() => navigate('/dashboard')}>
              Return to Dashboard
            </Button>
          </ErrorState>
        </PageContainer>
      </div>
    );
  }

  // Render sleep plan not found state
  if (!sleepPlan || !profile) {
    return (
      <div className="container">
        <PageContainer>
          <PageHeader>
            <Link to="/dashboard" className="back-link">
              <i className="fas fa-arrow-left me-2"></i> Back to Dashboard
            </Link>
          </PageHeader>
          
          <ErrorState>
            <div className="error-icon">
              <i className="fas fa-search"></i>
            </div>
            <h3>No Sleep Plan Found</h3>
            <p>
              We couldn't find a sleep plan for this profile. It may still be generating,
              or you might need to update the profile to create a new sleep plan.
            </p>
            <Button variant="primary" onClick={() => navigate(`/child/${profileId}`)}>
              Update Profile
            </Button>
          </ErrorState>
        </PageContainer>
      </div>
    );
  }

  // Format the creation date
  const creationDate = new Date(sleepPlan.createdAt).toLocaleDateString('en-AU', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="container">
      {/* Add print styles */}
      <PrintStyles />
      <PageContainer>
        <PageHeader>
          <Link to="/dashboard" className="back-link">
            <i className="fas fa-arrow-left me-2"></i> Back to Dashboard
          </Link>
          
        </PageHeader>
        
        <PlanCard className="PlanCard">
          {/* Personalized Greeting Section */}
          <WelcomeHeader>
            <h4>Sleep Plan for {profile.babyName}</h4>
            
            <div className="baby-details">
              <div className="detail-item">
                <span className="icon"><i className="fas fa-baby"></i></span>
                <span className="label">Age:</span>
                <span className="value">{calculateAge(profile.dateOfBirth)}</span>
              </div>
              
              <div className="detail-item">
                <span className="icon"><i className="fas fa-calendar-alt"></i></span>
                <span className="label">Created:</span>
                <span className="value">{creationDate}</span>
              </div>
              
              {sleepPlan.version > 1 && (
                <div className="detail-item">
                  <span className="icon"><i className="fas fa-code-branch"></i></span>
                  <span className="label">Version:</span>
                  <span className="value">{sleepPlan.version}</span>
                </div>
              )}
            </div>
            
            <p className="trusted-sources">
              This plan was generated based on trusted Australian sleep guidelines, and it's tailored to your responses about <b>{profile.babyName}'s </b>  
              current sleep patterns, development, and your parenting preferences.<br></br>
              This is not medical advice. Please consult a healthcare professional for medical concerns.
            </p>
          </WelcomeHeader>
          
          {/* Sleep Plan Content */}
          <ContentSection>
            {sleepPlan.content && typeof sleepPlan.content === 'string' ? (
              <div className="markdown-content">
                <ReactMarkdown>{sleepPlan.content}</ReactMarkdown>
              </div>
            ) : (
              <div style={{
                textAlign: 'center',
                padding: '3rem 2rem',
                backgroundColor: 'white',
                borderRadius: '16px',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
                margin: '2rem 0'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  border: '5px solid rgba(233, 64, 87, 0.2)',
                  borderRadius: '50%',
                  borderTopColor: 'var(--primary)',
                  animation: 'spin 1s ease-in-out infinite',
                  margin: '0 auto 2rem'
                }}></div>
                <h3 style={{
                  color: 'var(--primary)',
                  marginBottom: '1.5rem',
                  fontSize: '1.5rem',
                  fontWeight: '600',
                  position: 'static',
                  paddingBottom: '0',
                  borderBottom: 'none'
                }}>Creating Your Personalized Sleep Plan</h3>
                <p style={{
                  color: 'var(--gray)',
                  marginBottom: '2rem',
                  maxWidth: '500px',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  lineHeight: '1.6'
                }}>
                  We're analyzing your responses and creating a tailored sleep plan for {profile.babyName}.
                  This process typically takes about 5 minutes to complete. The page will automatically update when your plan is ready.
                </p>
              </div>
            )}
          </ContentSection>
          
          {/* Final Buttons */}
          <ButtonGroup>
            <Button variant="primary" onClick={handlePrintPlan}>
              Print Sleep Plan
            </Button>
          </ButtonGroup>
          
          {/* Parent Mental Health Support Box */}
          <ParentSupportBox />
          
        </PlanCard>
      </PageContainer>
    </div>
  );
};

export default SleepPlanViewer;
